import type { BoxProps } from '@chakra-ui/react';
import { Box, Separator } from '@chakra-ui/react';
import React from 'react';
import { useFilterButtonContext } from './useFilterButtonContext';

interface FilterButtonActiveOptionsProps extends BoxProps {
  children: React.ReactNode;
  /** Shows the value while the filter rests on its default too, so the brand reads what is shown. */
  showWhenResting?: boolean;
}

/**
 * The value half of an active filter trigger: a short divider after the label,
 * then the picked value in the trigger's own ink. Callers hand over the value
 * only — the divider and the typography live here, so every filter reads the
 * same and a value never fades to grey next to its own label.
 */
export const FilterButtonActiveOptions: React.FC<FilterButtonActiveOptionsProps> = ({
  children,
  showWhenResting = false,
  ...boxProps
}) => {
  const { isFilterActive } = useFilterButtonContext();

  if (!isFilterActive && !showWhenResting) {
    return null;
  }

  return (
    <>
      <Separator orientation="vertical" height="12px" borderColor="hairline.control" mx={1} />
      <Box
        as="span"
        ml={1}
        display="inline-flex"
        alignItems="center"
        fontFamily="heading"
        fontSize="12.5px"
        fontWeight="semibold"
        color="primary.900"
        {...boxProps}
      >
        {children}
      </Box>
    </>
  );
};
